import type { CustomerLanguage } from './customers';

/**
 * Customer communication: what a shop says to a customer, and what it is allowed to say.
 *
 * The delivery target is automated WhatsApp through Baileys, reached behind a provider abstraction
 * so no business rule here ever names it. One branch messages from one identity at a time; a second
 * number may be configured for continuity and is only ever selected by a person. When automation is
 * unavailable for any reason, a prepared message can still be handed to staff — the shop keeps
 * working.
 *
 * Two rules run through everything below. **Purpose decides policy**: a caller never gets to claim
 * its own message is operational. And **a status exists only when the platform can prove it**:
 * there is no DELIVERED or READ here, because Phase 5.3 has no live session against which WhatsApp
 * receipts could be verified.
 */

export const MarketingConsentStatus = {
  NOT_GRANTED: 'NOT_GRANTED',
  GRANTED: 'GRANTED',
  REVOKED: 'REVOKED',
} as const;
export type MarketingConsentStatus =
  (typeof MarketingConsentStatus)[keyof typeof MarketingConsentStatus];

export const CustomerPreferenceType = {
  OPERATIONAL_MESSAGING: 'OPERATIONAL_MESSAGING',
  MARKETING_CONSENT: 'MARKETING_CONSENT',
} as const;
export type CustomerPreferenceType =
  (typeof CustomerPreferenceType)[keyof typeof CustomerPreferenceType];

/** No `CUSTOMER`: there is no customer portal, and a value nothing can produce would mislead whoever reads the history. */
export const PreferenceChangeSource = {
  STAFF: 'STAFF',
  IMPORT: 'IMPORT',
  SYSTEM: 'SYSTEM',
} as const;
export type PreferenceChangeSource =
  (typeof PreferenceChangeSource)[keyof typeof PreferenceChangeSource];

export const CommunicationChannel = { WHATSAPP: 'WHATSAPP' } as const;
export type CommunicationChannel = (typeof CommunicationChannel)[keyof typeof CommunicationChannel];

export const CommunicationDirection = { OUTBOUND: 'OUTBOUND' } as const;
export type CommunicationDirection =
  (typeof CommunicationDirection)[keyof typeof CommunicationDirection];

export const CommunicationPurpose = {
  READY_FOR_COLLECTION: 'READY_FOR_COLLECTION',
  BIRTHDAY: 'BIRTHDAY',
  FOLLOW_UP: 'FOLLOW_UP',
  GENERAL: 'GENERAL',
} as const;
export type CommunicationPurpose = (typeof CommunicationPurpose)[keyof typeof CommunicationPurpose];

export const CommunicationDeliveryMode = {
  MANUAL_HANDOFF: 'MANUAL_HANDOFF',
  AUTOMATED_PROVIDER: 'AUTOMATED_PROVIDER',
} as const;
export type CommunicationDeliveryMode =
  (typeof CommunicationDeliveryMode)[keyof typeof CommunicationDeliveryMode];

export const CustomerCommunicationStatus = {
  PREPARED: 'PREPARED',
  QUEUED: 'QUEUED',
  SENDING: 'SENDING',
  PROVIDER_ACCEPTED: 'PROVIDER_ACCEPTED',
  HANDED_OFF: 'HANDED_OFF',
  CONFIRMED_SENT: 'CONFIRMED_SENT',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
  SUPPRESSED: 'SUPPRESSED',
} as const;
export type CustomerCommunicationStatus =
  (typeof CustomerCommunicationStatus)[keyof typeof CustomerCommunicationStatus];

/** Nothing moves out of these. */
export const TERMINAL_COMMUNICATION_STATUSES: readonly CustomerCommunicationStatus[] = [
  'PROVIDER_ACCEPTED',
  'CONFIRMED_SENT',
  'CANCELLED',
  'SUPPRESSED',
];

/**
 * Every move a communication may make, by delivery mode.
 *
 * Phase 5.3 drives none of these — it exposes no transition route. The map exists so the domain,
 * its tests and Phase 5.4's workflow all read the same definition rather than three similar ones.
 * `FAILED -> QUEUED` is a bounded retry the dispatcher owns; it is not a way back into preparation.
 */
export const COMMUNICATION_TRANSITIONS: Readonly<
  Record<CustomerCommunicationStatus, readonly CustomerCommunicationStatus[]>
> = {
  PREPARED: ['QUEUED', 'HANDED_OFF', 'CANCELLED', 'SUPPRESSED'],
  QUEUED: ['SENDING', 'CANCELLED', 'SUPPRESSED'],
  SENDING: ['PROVIDER_ACCEPTED', 'FAILED'],
  FAILED: ['QUEUED'],
  HANDED_OFF: ['CONFIRMED_SENT'],
  PROVIDER_ACCEPTED: [],
  CONFIRMED_SENT: [],
  CANCELLED: [],
  SUPPRESSED: [],
};

export function canTransitionCommunication(
  from: CustomerCommunicationStatus,
  to: CustomerCommunicationStatus,
): boolean {
  return COMMUNICATION_TRANSITIONS[from].includes(to);
}

export const CommunicationSuppressionReason = {
  NO_PHONE: 'NO_PHONE',
  INVALID_PHONE: 'INVALID_PHONE',
  PHONE_ROUTING_NOT_CONFIGURED: 'PHONE_ROUTING_NOT_CONFIGURED',
  CUSTOMER_OPERATIONAL_SUPPRESSED: 'CUSTOMER_OPERATIONAL_SUPPRESSED',
  MARKETING_NOT_CONSENTED: 'MARKETING_NOT_CONSENTED',
  TEMPLATE_INACTIVE: 'TEMPLATE_INACTIVE',
  RELATED_ENTITY_NO_LONGER_ELIGIBLE: 'RELATED_ENTITY_NO_LONGER_ELIGIBLE',
  BRANCH_WHATSAPP_DISABLED: 'BRANCH_WHATSAPP_DISABLED',
  WHATSAPP_SESSION_UNAVAILABLE: 'WHATSAPP_SESSION_UNAVAILABLE',
  /** A template names an allowed variable this message's context cannot resolve. Never sent with the token. */
  TEMPLATE_VARIABLE_UNAVAILABLE: 'TEMPLATE_VARIABLE_UNAVAILABLE',
} as const;
export type CommunicationSuppressionReason =
  (typeof CommunicationSuppressionReason)[keyof typeof CommunicationSuppressionReason];

// ============================================================================
// Policy: what a purpose means
// ============================================================================

export const CommunicationPolicyClass = {
  /** Rides the business relationship. Eligible unless the customer has asked the shop to stop. */
  OPERATIONAL: 'OPERATIONAL',
  /** Needs a recorded yes before anything goes out. */
  MARKETING: 'MARKETING',
} as const;
export type CommunicationPolicyClass =
  (typeof CommunicationPolicyClass)[keyof typeof CommunicationPolicyClass];

/**
 * The one mapping from purpose to policy.
 *
 * It lives here, beside the purpose it classifies, so a client cannot relabel a birthday greeting
 * as operational and message somebody who never consented to marketing. Callers pass a purpose;
 * nothing anywhere accepts a policy class as input.
 */
export const COMMUNICATION_PURPOSE_POLICY: Readonly<
  Record<CommunicationPurpose, CommunicationPolicyClass>
> = {
  READY_FOR_COLLECTION: 'OPERATIONAL',
  FOLLOW_UP: 'OPERATIONAL',
  // Customer service in V1: a shop answering a question it was asked, not a campaign.
  GENERAL: 'OPERATIONAL',
  BIRTHDAY: 'MARKETING',
};

export function policyClassOf(purpose: CommunicationPurpose): CommunicationPolicyClass {
  return COMMUNICATION_PURPOSE_POLICY[purpose];
}

// ============================================================================
// Template variables
// ============================================================================

export const TemplateVariableCategory = {
  CUSTOMER: 'CUSTOMER',
  BRANCH: 'BRANCH',
  OPTICAL_JOB: 'OPTICAL_JOB',
  SALES_ORDER: 'SALES_ORDER',
  GENERAL: 'GENERAL',
} as const;
export type TemplateVariableCategory =
  (typeof TemplateVariableCategory)[keyof typeof TemplateVariableCategory];

/**
 * One placeholder a template may use.
 *
 * `purposes` is what can actually resolve it: a token is offered to a purpose only when every message
 * of that purpose carries the context the token reads. The resolver and formatter live on the server
 * (`modules/crm/template-variables.ts`), keyed by the same token; a test fails if the two lists
 * differ.
 */
export interface TemplateVariableDefinition {
  token: string;
  category: TemplateVariableCategory;
  /** i18n keys, `crm.templates.tokens.<token>.label` and `.description`. */
  labelKey: string;
  descriptionKey: string;
  purposes: readonly CommunicationPurpose[];
}

const ALL_PURPOSES: readonly CommunicationPurpose[] = [
  'READY_FOR_COLLECTION',
  'FOLLOW_UP',
  'GENERAL',
  'BIRTHDAY',
];
const JOB_PURPOSES: readonly CommunicationPurpose[] = ['READY_FOR_COLLECTION'];

function variable(
  token: string,
  category: TemplateVariableCategory,
  purposes: readonly CommunicationPurpose[],
): TemplateVariableDefinition {
  return {
    token,
    category,
    labelKey: `crm.templates.tokens.${token}.label`,
    descriptionKey: `crm.templates.tokens.${token}.description`,
    purposes,
  };
}

/**
 * The canonical allow-list. Explicit and flat: every token is registered here by name, there is no
 * property path, and nothing outside this list can ever be substituted.
 *
 * Deliberately absent: the customer's own mobile number (it is the recipient, and would freeze
 * personal data into the stored body); payment amounts, dates and due dates, and the balance due
 * (no current purpose carries a payment or plan, and a balance is live financial state while a body
 * is frozen at preparation — they belong to a future reminder purpose with snapshot semantics); and
 * anything clinical, identifying, internal or provider-related, which no resolver reads.
 */
export const TEMPLATE_VARIABLES: readonly TemplateVariableDefinition[] = [
  variable('customerName', 'CUSTOMER', ALL_PURPOSES),
  variable('customerFirstName', 'CUSTOMER', ALL_PURPOSES),
  variable('branchName', 'BRANCH', ALL_PURPOSES),
  variable('branchPhone', 'BRANCH', ALL_PURPOSES),
  variable('branchAddress', 'BRANCH', ALL_PURPOSES),
  variable('opticalJobNumber', 'OPTICAL_JOB', JOB_PURPOSES),
  variable('opticalJobStatus', 'OPTICAL_JOB', JOB_PURPOSES),
  variable('readyDate', 'OPTICAL_JOB', JOB_PURPOSES),
  variable('salesOrderNumber', 'SALES_ORDER', JOB_PURPOSES),
  variable('orderDate', 'SALES_ORDER', JOB_PURPOSES),
  variable('orderTotal', 'SALES_ORDER', JOB_PURPOSES),
  variable('todayDate', 'GENERAL', ALL_PURPOSES),
];

export const TEMPLATE_VARIABLE_TOKENS: readonly string[] = TEMPLATE_VARIABLES.map((v) => v.token);

/** What a template body may name, by purpose — derived from the registry, never listed twice. */
const tokensFor = (purpose: CommunicationPurpose): readonly string[] =>
  TEMPLATE_VARIABLES.filter((v) => v.purposes.includes(purpose)).map((v) => v.token);

export const TEMPLATE_VARIABLES_BY_PURPOSE: Readonly<
  Record<CommunicationPurpose, readonly string[]>
> = {
  READY_FOR_COLLECTION: tokensFor('READY_FOR_COLLECTION'),
  FOLLOW_UP: tokensFor('FOLLOW_UP'),
  GENERAL: tokensFor('GENERAL'),
  BIRTHDAY: tokensFor('BIRTHDAY'),
};

/** `{{ name }}`, with optional inner spacing. Nothing else is a placeholder. */
export const TEMPLATE_VARIABLE_PATTERN = /\{\{\s*([A-Za-z][A-Za-z0-9]*)\s*\}\}/g;

/** Every placeholder a body names, in order of appearance, without duplicates. */
export function templateVariablesUsed(body: string): string[] {
  const found = new Set<string>();
  for (const match of body.matchAll(TEMPLATE_VARIABLE_PATTERN)) {
    if (match[1]) found.add(match[1]);
  }
  return [...found];
}

/** The placeholders a body names that its purpose does not offer. Empty means none. */
export function unknownTemplateVariables(body: string, purpose: CommunicationPurpose): string[] {
  const allowed = TEMPLATE_VARIABLES_BY_PURPOSE[purpose];
  return templateVariablesUsed(body).filter((name) => !allowed.includes(name));
}

/**
 * Brace fragments that are not a well-formed placeholder: `{{customer.name}}`, `{{ a b }}`, a lone
 * `{{` or `}}`. Such text would otherwise be sent to a customer exactly as typed, so a body holding
 * any is refused. Returned as written, for the error message.
 */
export function malformedTemplateFragments(body: string): string[] {
  const withoutValid = body.replace(TEMPLATE_VARIABLE_PATTERN, '');
  const fragments = withoutValid.match(/\{\{[^{}]*\}\}|\{\{|\}\}/g) ?? [];
  return [...new Set(fragments)];
}

// ============================================================================
// WhatsApp identity and session health
// ============================================================================

export const WhatsAppProviderType = { BAILEYS: 'BAILEYS' } as const;
export type WhatsAppProviderType = (typeof WhatsAppProviderType)[keyof typeof WhatsAppProviderType];

export const WhatsAppIdentitySlot = {
  PRIMARY: 'PRIMARY',
  ALTERNATIVE: 'ALTERNATIVE',
} as const;
export type WhatsAppIdentitySlot = (typeof WhatsAppIdentitySlot)[keyof typeof WhatsAppIdentitySlot];

/**
 * The observed state of a branch's WhatsApp session.
 *
 * There is no `BANNED`: WhatsApp provides no signal that reliably means it, and a label the
 * platform would have to infer is worse than none. `RESTRICTED` is written only from an explicit
 * provider signal, never from a failed message.
 */
export const WhatsAppSessionHealth = {
  NOT_CONFIGURED: 'NOT_CONFIGURED',
  DISCONNECTED: 'DISCONNECTED',
  CONNECTING: 'CONNECTING',
  QR_REQUIRED: 'QR_REQUIRED',
  CONNECTED: 'CONNECTED',
  DEGRADED: 'DEGRADED',
  LOGGED_OUT: 'LOGGED_OUT',
  RESTRICTED: 'RESTRICTED',
  ERROR: 'ERROR',
} as const;
export type WhatsAppSessionHealth =
  (typeof WhatsAppSessionHealth)[keyof typeof WhatsAppSessionHealth];

/**
 * The only state from which automated dispatch may run.
 *
 * Everything else stops automation and waits for a person. `DEGRADED` is excluded deliberately:
 * "probably fine" is not a basis on which to message a stranger's phone. `LOGGED_OUT` and
 * `RESTRICTED` additionally require re-authentication or investigation — and **never** an automatic
 * move to the branch's alternative number, which would be working around an enforcement signal
 * rather than responding to it.
 */
export function automationMayRun(health: WhatsAppSessionHealth): boolean {
  return health === WhatsAppSessionHealth.CONNECTED;
}

/** States that need a person before anything is sent again from this identity. */
export const WHATSAPP_HEALTH_NEEDS_INTERVENTION: readonly WhatsAppSessionHealth[] = [
  'QR_REQUIRED',
  'LOGGED_OUT',
  'RESTRICTED',
  'ERROR',
];

// ============================================================================
// Provider abstraction
// ============================================================================

/** What a provider says about the identity it is authenticated as. No credential ever appears here. */
export interface WhatsAppIdentityInfo {
  /** Digits only, as `normalizePhone` produces. */
  phoneNormalized: string;
  providerType: WhatsAppProviderType;
}

export interface WhatsAppProviderHealth {
  state: WhatsAppSessionHealth;
  lastConnectedAt: string | null;
  /** A mapped code, never a provider message and never a stack. */
  lastFailureCode: string | null;
}

export interface WhatsAppSendRequest {
  /** Digits only. The caller has already resolved and validated it. */
  toNormalized: string;
  body: string;
}

/**
 * What the provider proved, and nothing more. `PROVIDER_ACCEPTED` means the server took the
 * message — it is **not** delivery, and nothing may infer delivery from it.
 *
 * The four outcomes differ in the one thing that decides whether a retry is safe:
 * - `NOT_SENT` — the message was never handed to the provider. Retrying the same attempt, with the
 *   same provider message id, cannot duplicate anything.
 * - `REJECTED` — the provider definitely refused it before accepting it. Nothing reached the
 *   customer; `retryable` says whether a later, new attempt may succeed.
 * - `AMBIGUOUS` — the message was handed over and the outcome is unknown. It is never retried
 *   automatically, because "probably not sent" is how a customer gets the same message twice.
 */
export type WhatsAppSendResult =
  | { outcome: 'PROVIDER_ACCEPTED' }
  | { outcome: 'NOT_SENT'; reason: string }
  | { outcome: 'REJECTED'; failureCode: string; retryable: boolean }
  | { outcome: 'AMBIGUOUS'; failureCode: string };

/**
 * The seam between CRM and whatever carries a message.
 *
 * Phase 5.3 defines it and implements none of it. Phase 5.4 supplies a Baileys implementation; a
 * hosted API could supply another without a line of CRM logic changing. Nothing in this interface
 * exposes session state, keys or QR payloads: those live in Phase 5.4's encrypted storage and never
 * cross this boundary.
 */
export interface WhatsAppProvider {
  readonly providerType: WhatsAppProviderType;
  connect(branchId: string): Promise<WhatsAppProviderHealth>;
  disconnect(branchId: string): Promise<void>;
  health(branchId: string): Promise<WhatsAppProviderHealth>;
  getIdentity(branchId: string): Promise<WhatsAppIdentityInfo | null>;
  sendMessage(branchId: string, request: WhatsAppSendRequest): Promise<WhatsAppSendResult>;
}

// ============================================================================
// Phone routing
// ============================================================================

export type PhoneRoutingFailure = 'NO_PHONE' | 'PHONE_ROUTING_NOT_CONFIGURED' | 'INVALID_PHONE';

/** Either a number the platform is willing to message, or exactly why it will not guess one. */
export type RoutableMobile =
  { ok: true; phoneNormalized: string } | { ok: false; reason: PhoneRoutingFailure };

// ============================================================================
// API shapes
// ============================================================================

export interface ContactPreferenceView {
  customerId: string;
  operationalMessagingSuppressed: boolean;
  marketingConsentStatus: MarketingConsentStatus;
  /** 0 while the customer has no stored row: the defaults are the policy until somebody changes them. */
  version: number;
  updatedAt: string | null;
}

export interface PreferenceChangeView {
  id: string;
  preferenceType: CustomerPreferenceType;
  previousValue: string;
  newValue: string;
  source: PreferenceChangeSource;
  changedByUserId: string | null;
  changedAt: string;
}

export interface CommunicationTemplateSummary {
  id: string;
  code: string;
  name: string;
  nameAr: string | null;
  channel: CommunicationChannel;
  purpose: CommunicationPurpose;
  isActive: boolean;
  version: number;
}

export interface CommunicationTemplateDetail extends CommunicationTemplateSummary {
  bodyEn: string;
  bodyAr: string;
  /** What this purpose allows, so an editor can show the list rather than guess it. */
  allowedVariables: readonly string[];
  createdAt: string;
  updatedAt: string;
}

/** One attempt to hand a message to a provider, as a screen reads it. */
export interface CommunicationAttemptView {
  id: string;
  attemptNumber: number;
  status: CommunicationDeliveryAttemptStatus;
  providerType: WhatsAppProviderType;
  identitySlot: WhatsAppIdentitySlot;
  /** The number this attempt sent from. Historical: a later switch never rewrites it. */
  senderPhoneSnapshot: string | null;
  recipientSnapshot: string | null;
  /** A mapped code, never a provider message. */
  safeFailureCode: string | null;
  queuedAt: string;
  startedAt: string | null;
  providerAcceptedAt: string | null;
  failedAt: string | null;
}

export interface CustomerCommunicationSummary {
  id: string;
  customerId: string;
  customerName: string;
  branchId: string;
  branchName: string;
  channel: CommunicationChannel;
  direction: CommunicationDirection;
  purpose: CommunicationPurpose;
  deliveryMode: CommunicationDeliveryMode;
  status: CustomerCommunicationStatus;
  locale: CustomerLanguage;
  templateId: string | null;
  templateVersion: number | null;
  suppressionReason: CommunicationSuppressionReason | null;
  salesOrderId: string | null;
  opticalJobId: string | null;
  preparedAt: string;
}

export interface CustomerCommunicationDetail extends CustomerCommunicationSummary {
  /** The words the customer was, or will be, shown. Frozen at preparation. */
  bodySnapshot: string | null;
  /** Null until the message actually goes out. */
  recipientSnapshot: string | null;
  senderIdentitySlot: WhatsAppIdentitySlot | null;
  senderPhoneSnapshot: string | null;
  senderProviderType: WhatsAppProviderType | null;
  failureCode: string | null;
  initiatedByUserId: string | null;
  queuedAt: string | null;
  sendingAt: string | null;
  providerAcceptedAt: string | null;
  handedOffAt: string | null;
  confirmedSentAt: string | null;
  failedAt: string | null;
  cancelledAt: string | null;
  suppressedAt: string | null;
  attempts: CommunicationAttemptView[];
}

/** What the workspace header counts. Deliberately no delivered or read bucket. */
export interface CommunicationSummaryCounts {
  /** Decided and waiting: PREPARED or QUEUED or SENDING. */
  pending: number;
  providerAccepted: number;
  suppressed: number;
  /** FAILED or CANCELLED — the ones a person may need to do something about. */
  failed: number;
  /** Manual messages a person still has to finish: HANDED_OFF. */
  awaitingManual: number;
  confirmedSent: number;
}

export interface CustomerReminderView {
  id: string;
  customerId: string;
  branchId: string;
  type: CustomerReminderType;
  dueDate: string;
  status: CustomerReminderStatus;
  note: string | null;
  assignedToUserId: string | null;
  createdByUserId: string;
  completedAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
}

export const CustomerReminderType = {
  FOLLOW_UP: 'FOLLOW_UP',
  CALL: 'CALL',
  COLLECTION_FOLLOW_UP: 'COLLECTION_FOLLOW_UP',
  OTHER: 'OTHER',
} as const;
export type CustomerReminderType = (typeof CustomerReminderType)[keyof typeof CustomerReminderType];

export const CustomerReminderStatus = {
  OPEN: 'OPEN',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const;
export type CustomerReminderStatus =
  (typeof CustomerReminderStatus)[keyof typeof CustomerReminderStatus];

/**
 * A customer on a birthday list, and nothing else about them.
 *
 * Deliberately narrow: this answers "whose birthday is it" for staff awareness. Nothing clinical,
 * nothing financial, and no address — a list screen has no business carrying any of it.
 */
export interface BirthdayEntry {
  customerId: string;
  customerNumber: string;
  displayName: string;
  preferredLanguage: CustomerLanguage;
  /** The day it falls on this year, already resolved — 29 February is observed on the 28th outside a leap year. */
  observedOn: string;
  /** Whether a birthday message would be allowed at all. Marketing needs a recorded yes. */
  marketingConsentStatus: MarketingConsentStatus;
}

export interface BranchWhatsAppConfigView {
  id: string;
  branchId: string;
  enabled: boolean;
  primaryPhoneNumber: string;
  primaryPhoneNormalized: string;
  alternativePhoneNumber: string | null;
  alternativePhoneNormalized: string | null;
  activeIdentity: WhatsAppIdentitySlot;
  providerType: WhatsAppProviderType;
  automationEnabled: boolean;
  manualFallbackEnabled: boolean;
  sessionHealth: WhatsAppSessionHealth;
  lastConnectedAt: string | null;
  lastSuccessfulSendAt: string | null;
  lastFailureAt: string | null;
  lastFailureCode: string | null;
  /** Derived, never stored: configuration alone does not make a branch able to send. */
  automationEligible: boolean;
  version: number;
}

export interface TenantPhoneSettingsView {
  defaultCountryCallingCode: string | null;
  phoneTrunkPrefix: string | null;
}

/**
 * The two keys that make the READY_FOR_COLLECTION automation replay-safe.
 *
 * Both are built from the same pair — the job and the exact status change that made it collectable
 * — and both live here so the producer in `sales` and the handler in `crm` cannot drift apart. A
 * key derived from a clock or a random id would make every replay a new message, which is the one
 * failure a customer actually notices.
 *
 * `seq` is `OpticalJobStatusChange.seq`, a BIGSERIAL, so it is stable, ordered and never reused.
 */
export function readyForCollectionDedupeKey(opticalJobId: string, seq: bigint | string): string {
  return `optical-job:${opticalJobId}:ready:${seq}`;
}

export function readyForCollectionCommunicationKey(
  opticalJobId: string,
  seq: bigint | string,
): string {
  return `comm:ready:${opticalJobId}:${seq}`;
}

/**
 * Which automated message a rule governs.
 *
 * One value, and deliberately not a generic event-to-message rule builder. Every entry here is a
 * trigger somebody implemented, reviewed and can explain to a customer who asks why they were
 * messaged; a rule engine that could wire any event to any template would remove exactly that
 * accountability.
 */
export const CommunicationAutomationType = {
  READY_FOR_COLLECTION: 'READY_FOR_COLLECTION',
} as const;
export type CommunicationAutomationType =
  (typeof CommunicationAutomationType)[keyof typeof CommunicationAutomationType];

/** One attempt's place in a message's delivery history. */
export const CommunicationDeliveryAttemptStatus = {
  QUEUED: 'QUEUED',
  SENDING: 'SENDING',
  PROVIDER_ACCEPTED: 'PROVIDER_ACCEPTED',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
  OUTCOME_AMBIGUOUS: 'OUTCOME_AMBIGUOUS',
} as const;
export type CommunicationDeliveryAttemptStatus =
  (typeof CommunicationDeliveryAttemptStatus)[keyof typeof CommunicationDeliveryAttemptStatus];

/**
 * An automation rule as a settings screen reads it.
 *
 * `enabled` and `templateActive` are separate on purpose: a shop may switch a rule on and save it
 * before anybody has paired a phone or while its template is being revised, and conflating
 * "configured" with "will actually send" would leave somebody believing customers are being told
 * something when they are not.
 */
export interface CommunicationAutomationView {
  id: string;
  branchId: string | null;
  type: CommunicationAutomationType;
  enabled: boolean;
  templateId: string | null;
  templateCode: string | null;
  templateName: string | null;
  templateActive: boolean;
  version: number;
}
